import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, Crown, Loader2, Trophy, Award, Clock, X, ChevronRight,
  UserCog, UserPlus, Trash2, Check, Settings as Cog,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METRICS = ["optimizations", "applications", "shortlisted", "interviews", "offers", "rejections"];
const METRIC_LABELS = {
  optimizations: "Optimizations", applications: "Applications", shortlisted: "Shortlisted",
  interviews: "Interviews", offers: "Offers", rejections: "Rejections",
};
const RANGES = [{ k: "day", l: "Today" }, { k: "week", l: "7d" }, { k: "15day", l: "15d" }, { k: "month", l: "30d" }, { k: "all", l: "All" }];

const chip = (active) => ({
  padding: "0.42rem 0.9rem", borderRadius: 999, fontSize: "0.82rem", cursor: "pointer",
  border: `1.5px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
  fontWeight: active ? 600 : 400, transition: "all 0.15s",
  display: "inline-flex", alignItems: "center", gap: 6,
});

export default function AgencyTeamPage() {
  const { agencyId, teamId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("week");
  const [drill, setDrill] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [manage, setManage] = useState(false);
  const [allCands, setAllCands] = useState([]);

  const loadDash = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/agencies/${agencyId}/teams/${teamId}/dashboard?range=${range}`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Can't open this team");
      navigate(`/app/agencies/${agencyId}`);
    } finally { setLoading(false); }
  }, [agencyId, teamId, range, navigate]);
  const loadCands = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/agencies/${agencyId}/teams/${teamId}/candidates?range=${range}`);
      setCandidates(r.data.candidates || []);
    } catch { /* ignore */ }
  }, [agencyId, teamId, range]);

  useEffect(() => { loadDash(); loadCands(); }, [loadDash, loadCands]);
  useEffect(() => {
    axios.get(`${API}/agencies/${agencyId}`).then((r) => setIsAdmin(r.data.my_role === "admin")).catch(() => {});
  }, [agencyId]);

  const openDrill = async (c) => {
    setDrill({ loading: true });
    try { const r = await axios.get(`${API}/agencies/${agencyId}/candidates/${c.user_id}?range=${range}`); setDrill({ ...r.data, loading: false }); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); setDrill(null); }
  };

  const openManage = async () => {
    try {
      const r = await axios.get(`${API}/agencies/${agencyId}/candidates?range=all`);
      setAllCands(r.data.candidates || []);
      setManage(true);
    } catch { toast.error("Failed to load candidates"); }
  };
  const assign = async (uid) => {
    try { await axios.post(`${API}/agencies/${agencyId}/teams/${teamId}/candidates`, { user_id: uid }); toast.success("Added to team"); openManage(); loadDash(); loadCands(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const unassign = async (uid) => {
    try { await axios.post(`${API}/agencies/${agencyId}/teams/${teamId}/candidates/${uid}/remove`); toast.success("Removed from team"); openManage(); loadDash(); loadCands(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="analytics-loading" data-testid="team-loading"><div className="processing-spinner" /></div>;
  if (!data) return null;
  const t = data.team;

  return (
    <div className="page-container fade-in" data-testid="agency-team-page">
      <button className="btn-secondary" data-testid="team-back-btn" onClick={() => navigate(`/app/agencies/${agencyId}`)}><ArrowLeft size={16} /> Back</button>

      <div className="page-header" style={{ marginTop: "0.8rem" }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}><Users size={22} /> {t.name}</h1>
          <p style={{ color: "var(--text-secondary)" }}>
            {t.head ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><UserCog size={14} /> Head: {t.head.name} <span style={{ color: "var(--text-muted)" }}>@{t.head.username}</span></span> : "No team head assigned"}
            {t.description ? ` · ${t.description}` : ""}
          </p>
        </div>
        {isAdmin && <button className="btn-primary" data-testid="manage-team-btn" onClick={openManage}><Cog size={15} /> Manage candidates</button>}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }} data-testid="team-range-selector">
        {RANGES.map((r) => (
          <button key={r.k} data-testid={`team-range-${r.k}`} onClick={() => setRange(r.k)} style={chip(range === r.k)}>{r.l}</button>
        ))}
      </div>

      {/* Placed highlight */}
      <div className="card" data-testid="team-placed" style={{ padding: "1.1rem 1.3rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: 14, borderColor: "rgba(99,102,241,0.35)" }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(99,102,241,0.15)", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}><Award size={24} /></div>
        <div>
          <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>Candidates placed (with an offer)</div>
          <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#6366f1" }}>{data.candidates_placed} <span style={{ fontSize: "0.9rem", fontWeight: 500, color: "var(--text-secondary)" }}>/ {data.candidate_count} candidates</span></div>
        </div>
      </div>

      {/* Totals */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "0.8rem", marginBottom: "1.5rem" }}>
        {METRICS.map((m) => (
          <div key={m} className="card" data-testid={`team-total-${m}`} style={{ padding: "1.1rem" }}>
            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
            <div style={{ fontSize: "1.8rem", fontWeight: 700, marginTop: 4 }}>{data.totals?.[m] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Candidates table */}
      <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><Trophy size={18} /> Team candidates</h3>
      {!candidates ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
        : candidates.length === 0 ? (
          <div className="card" data-testid="team-candidates-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
            <Users size={32} style={{ opacity: 0.35 }} />
            <p>No candidates in this team yet.{isAdmin ? " Use 'Manage candidates' to add some." : ""}</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }} data-testid="team-candidates-table">
              <thead>
                <tr style={{ textAlign: "left", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                  <th style={{ padding: "0.8rem 1rem" }}>Candidate</th>
                  {METRICS.map((m) => <th key={m} style={{ padding: "0.8rem 1rem", textAlign: "right" }}>{METRIC_LABELS[m]}</th>)}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.user_id} data-testid={`team-candidate-${c.username}`} style={{ borderTop: "1px solid var(--border-main)", cursor: "pointer" }} onClick={() => openDrill(c)}>
                    <td style={{ padding: "0.7rem 1rem" }}>
                      <div style={{ fontWeight: 500 }}>{c.name || c.username}</div>
                      <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{c.username}</div>
                    </td>
                    {METRICS.map((m) => <td key={m} style={{ padding: "0.7rem 1rem", textAlign: "right" }}>{c.metrics?.[m] ?? 0}</td>)}
                    <td style={{ padding: "0.7rem 1rem", textAlign: "right" }}><ChevronRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Drill-down */}
      {drill && (
        <div className="modal-overlay" data-testid="team-drill-modal" onClick={() => setDrill(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(520px, 100%)" }}>
            {drill.loading ? <div style={{ textAlign: "center" }}><Loader2 size={20} className="spin" /></div> : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                  <div>
                    <h2 style={{ margin: 0 }}>{drill.candidate.name || drill.candidate.username}</h2>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>@{drill.candidate.username} · {RANGES.find((r) => r.k === range)?.l}</div>
                  </div>
                  <button onClick={() => setDrill(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.7rem" }}>
                  {METRICS.map((m) => (
                    <div key={m} data-testid={`team-drill-${m}`} style={{ padding: "0.9rem", borderRadius: 10, background: "var(--bg-surface-hl)" }}>
                      <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
                      <div style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: 2 }}>{drill.stats?.[m] ?? 0}</div>
                    </div>
                  ))}
                </div>
                {drill.stats?.mostly_applied_hour && (
                  <div style={{ marginTop: 12, fontSize: "0.84rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
                    <Clock size={14} /> Most active applying around <strong>{drill.stats.mostly_applied_hour}</strong>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Manage candidates (admin) */}
      {manage && (
        <div className="modal-overlay" data-testid="manage-team-modal" onClick={() => setManage(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.6rem", width: "min(540px, 100%)", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <h2 style={{ margin: 0 }}>Manage candidates · {t.name}</h2>
              <button onClick={() => setManage(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", marginTop: 0 }}>A candidate can be in one team at a time. Adding them here moves them from any other team.</p>
            {allCands.length === 0 ? <div style={{ color: "var(--text-muted)" }}>No consented candidates in this agency.</div>
              : allCands.map((c) => {
                const inThis = c.team_id === teamId;
                return (
                  <div key={c.user_id} data-testid={`manage-cand-${c.username}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.6rem 0", borderTop: "1px solid var(--border-main)" }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{c.name || c.username} <span style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{c.username}</span></div>
                      <div style={{ fontSize: "0.74rem", color: c.team_name ? "#f59e0b" : "var(--text-muted)" }}>{c.team_name ? `In: ${c.team_name}` : "Unassigned"}</div>
                    </div>
                    {inThis
                      ? <button className="btn-secondary" data-testid={`unassign-${c.username}`} onClick={() => unassign(c.user_id)} style={{ color: "#ef4444" }}><Trash2 size={14} /> Remove</button>
                      : <button className="btn-secondary" data-testid={`assign-${c.username}`} onClick={() => assign(c.user_id)}><UserPlus size={14} /> Add</button>}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
